import { Environment, isSourceType } from '@foundation-player/runtime';
import { isArrayOf } from '@foundation-player/utils';
import { TransformPluginConfigs } from './transform-plugin-configs';

/**
 * This function is used to evaluate the plugin configuration for the NPAW Analytics Plugin.
 *
 * @remarks
 * The plugin is enabled by default for all clients unless explicitly disabled via the plugin config or remote config.
 *
 * Be aware that since v7 the NPAW CDN Balancer is also part of the NPAW Analytics Plugin.
 * The CDN Balancer is enabled via remote config for certain clients, but only active if the NPAW Analytics Plugin is enabled as well.
 */
export const addNPAWAnalyticsPlugin: TransformPluginConfigs = (
  partialBuilderConfig,
  pluginConfigs,
  clientAndScope,
  environment,
  platform,
) => {
  const npawAnalytics = 'foundation.plugin.npawAnalytics';
  const ACCOUNT_CODE_RTL_DEV = 'rtlgermanydev';
  const ACCOUNT_CODE_RTL_PROD = 'rtlgermany';

  const { playerConfig, remoteConfig, isLocal, cdn } = partialBuilderConfig;

  if (pluginConfigs[npawAnalytics]?.enabled === false) {
    return pluginConfigs;
  }
  if (!pluginConfigs[npawAnalytics]?.enabled && remoteConfig.npawAnalytics?.enable === false) {
    return pluginConfigs;
  }

  const cdnBalancer =
    typeof pluginConfigs[npawAnalytics]?.cdnBalancer?.enabled === 'boolean'
      ? pluginConfigs[npawAnalytics].cdnBalancer.enabled
      : remoteConfig.npawCdnBalancer?.enable;

  const accountCodeFromConfigs = pluginConfigs[npawAnalytics]?.accountCode ?? remoteConfig.npawAnalytics?.accountCode;

  const fallbackAccountCode = environment === Environment.Production ? ACCOUNT_CODE_RTL_PROD : ACCOUNT_CODE_RTL_DEV;

  // note: use remote config as a fallback only
  // note: arrays won't be merged
  const enableForSourceTypes = isArrayOf(pluginConfigs[npawAnalytics]?.cdnBalancer?.enableForSourceTypes, isSourceType)
    ? pluginConfigs[npawAnalytics].cdnBalancer.enableForSourceTypes
    : isArrayOf(remoteConfig.npawCdnBalancer?.enableForSourceTypes, isSourceType)
      ? remoteConfig.npawCdnBalancer.enableForSourceTypes
      : [];

  return {
    ...pluginConfigs,
    [npawAnalytics]: {
      accountCode: accountCodeFromConfigs ?? fallbackAccountCode,
      appName: playerConfig.metadata?.appName || remoteConfig.npawAnalytics?.appName || clientAndScope?.client,
      appVersion: playerConfig.metadata?.appVersion,
      ...pluginConfigs[npawAnalytics],
      enabled: true,
      environment,
      isLocal,
      cdn,
      platform,
      cdnBalancer: cdnBalancer
        ? {
            enabled: enableForSourceTypes.length > 0,
            enableForSourceTypes,
            bucketName:
              pluginConfigs[npawAnalytics]?.cdnBalancer?.bucketName || remoteConfig.npawCdnBalancer?.originCode,
            debug: pluginConfigs[npawAnalytics]?.cdnBalancer?.debug,
          }
        : undefined,
    },
  };
};
